<template>
  <div>
    <form @submit.prevent="handlePost">
      <div class="flex flex-col items-center text-center mx-auto pt-6 pb-12 px-8 md:px-0">
        <p class="text-2xl">Please select most accurate for officer</p>

        <base-emoji
          v-model="officer.emoji"
          name="emoji"
          class="max-w-lg"
          :message="errors.first('emoji')"
          v-validate="'required'"
        />

        <section class="flex flex-wrap w-full max-w-5xl mt-4">
          <base-input
            v-model="officer.number"
            v-validate="'required|numeric'"
            name="number"
            class="flex-grow mx-4 my-2"
            placeholder="Officer #"
            :message="errors.first('number')"
            autofocus
          />
          <base-input
            v-model="officer.city"
            v-validate="'required|max:255'"
            name="city"
            class="flex-grow mx-4 my-2"
            placeholder="City"
            :message="errors.first('city')"
            :locationOptions="{ types: ['(cities)'] }"
            location
            @locationChanged="handleLocationChanged"
          />
          <base-input
            v-model="officer.county"
            v-validate="'required|max:255'"
            name="county"
            class="flex-grow mx-4 my-2"
            placeholder="County"
            :message="errors.first('county')"
          />
          <base-input
            v-model="officer.state"
            v-validate="'required'"
            name="state"
            class="flex-grow mx-4 my-2"
            placeholder="State"
            :message="errors.first('state')"
            readonly
          />
        </section>

        <section class="text-left">
          <div class="flex my-12">
            <span class="mt-2 w-40">Response Time</span>
            <base-rating v-model="rating.option1" />
          </div>
          <div class="flex my-12">
            <span class="mt-2 w-40">Communication</span>
            <base-rating v-model="rating.option2" />
          </div>
          <div class="flex my-12">
            <span class="mt-2 w-40">Professionalism</span>
            <base-rating v-model="rating.option3" />
          </div>
          <div class="flex my-12">
            <span class="mt-2 w-40">Attitude</span>
            <base-rating v-model="rating.option4" />
          </div>
          <div class="flex my-12">
            <span class="mt-2 w-40">Overall Experience </span>
            <base-rating v-model="rating.option5" />
          </div>
        </section>

        <base-input
          v-model="rating.review"
          v-validate="'required|max:255'"
          name="preview"
          type="textarea"
          class="w-full max-w-lg h-48 mt-4"
          placeholder="Enter a detailed description…"
          :message="errors.first('preview')"
        />

        <input
          ref="inputFile"
          accept=".png, .jpg, .jpeg, .mp4"
          type="file"
          hidden
          @change="handleFile"
        />

        <section class="w-full max-w-lg mt-4 flex flex-wrap">
          <div
            class="relative flex-grow w-1/4 h-32 m-2"
            v-for="(media, index) in medias"
            :key="index"
          >
            <i
              class="material-icons absolute left-0 text-red-500 ml-2 mt-2 z-50 cursor-pointer"
              v-if="!isLoading"
              @click="clearFile(media)"
            >close</i>

            <img
              class="object-cover h-full w-full"
              :src="media.preview"
              v-if="media.type === 'image'"
            />

            <video class="object-cover w-full h-full" v-else>
              <source :src="media.preview" />
            </video>
          </div>
        </section>

        <div class="flex w-full mt-8 max-w-lg">
          <button class="btn w-1/2" :disabled="isLoading">Post Rating</button>
          <button
            class="btn btn-blue w-1/2 ml-4"
            type="button"
            :disabled="isLoading"
            @click="$refs.inputFile.click()"
          >Add Photo/Video</button>
        </div>
      </div>
    </form>

    <base-footer />
  </div>
</template>

<script>
import "firebase/storage";
import uuid from "uuid/v1";
import firebase from "firebase/app";
import OfficerService from "@/services/OfficerService";
import LocationService from "@/services/core/LocationService";

export default {
  data() {
    return {
      officer: {},
      rating: { media: [] },
      medias: [],
      isLoading: false
    };
  },
  methods: {
    handleFile(e) {
      const file = e.target.files[0];

      if (file && !this.medias.some(media => media.file.name === file.name)) {
        const extension = file.name.split(".").pop();
        const type = ["png", "jpg", "jpeg"].includes(extension)
          ? "image"
          : "video";

        this.medias.push({
          file,
          preview: URL.createObjectURL(file),
          extension,
          type
        });
      }
    },

    clearFile(media) {
      this.medias.splice(this.medias.indexOf(media), 1);
      this.$refs.inputFile.value = "";
    },

    async handleLocationChanged(place) {
      this.officer.county = LocationService.findAddress(
        place,
        "administrative_area_level_2"
      ) || this.officer.city;
      this.officer.state = LocationService.findAddress(
        place,
        "administrative_area_level_1"
      );
    },

    async handlePost() {
      try {
        if (this.isLoading) {
          return;
        }

        if (!(await this.$validator.validateAll())) {
          this.$toasted.error("Please check the fields.");
          return;
        }

        if (
          !this.rating.option1 ||
          !this.rating.option2 ||
          !this.rating.option3 ||
          !this.rating.option4 ||
          !this.rating.option5
        ) {
          this.$toasted.error("Complete the rating");
          return;
        }

        this.isLoading = true;

        // Create Officer
        const {
          data: { data }
        } = await OfficerService.create(this.officer);

        // Upload rating media
        await Promise.all(
          this.medias.map(async media => {
            const fileName = `${uuid()}.${media.extension}`;
            const snapshot = await firebase
              .storage()
              .ref(`cops/${fileName}`)
              .put(media.file);

            this.rating.media.push({
              name: fileName,
              url: await snapshot.ref.getDownloadURL(),
              type: media.type
            });
          })
        );

        // Rate cop
        await OfficerService.rate(data, this.rating);

        this.$toasted.show("Cop & Rating published");
        this.$router.push({ name: "cops" });
      } catch (e) {
        this.$setErrorsFromLaravel(e.response.data);
      } finally {
        this.isLoading = false;
      }
    }
  }
};
</script>
